/**
 * Storage backend contract for the Smart CMS.
 *
 * Supabase is the only backend. The interface remains because the three CMS
 * stores are pure synchronous functions -- they take a store and return a new
 * one -- and the hooks call load()/save() at exactly one seam. Keeping
 * persistence behind that seam is what avoided rewriting ~60 store functions
 * to be async and re-testing every caller and agent tool.
 *
 * localStorage is still written on every save, but only as a read-through
 * cache so the UI has something synchronous to render immediately. The
 * database is authoritative: it wins on load, and a local copy is never
 * treated as a substitute for a successful write.
 */

export type Backend = "articles" | "service-map" | "site-config"

/**
 * Container-row id for a team's store.
 *
 * The hooks pass `teamId` as their dbId (it namespaces the localStorage keys),
 * and the adapters use that same value as the container row's PRIMARY KEY.
 * Using the bare team id would make scms_kb.id and scms_kb.team_id identical,
 * which reads as a mistake and blocks a second KB per team later.
 *
 * Prefixing keeps one container per team today while leaving room for
 * `${teamId}:second-kb` tomorrow, without a migration.
 */
export function containerId(backend: Backend, teamId: string): string {
  return `${backend}:${teamId}`
}

export interface StoreAdapter<TStore> {
  /**
   * Reads the whole store. Returns null when nothing is stored yet, so the
   * caller can distinguish "empty" from "never created" and seed accordingly.
   */
  load(dbId: string): Promise<TStore | null>

  /**
   * Writes the whole store.
   *
   * Deliberately whole-store rather than per-entity: the callers already hold
   * a complete new store (that is what the pure store functions return), and
   * a diffing layer would be a second source of truth about what changed.
   * The Supabase adapter diffs internally to avoid rewriting every row.
   */
  save(dbId: string, store: TStore): Promise<void>

  /** Removes everything for this store. Used by the reset action. */
  clear(dbId: string): Promise<void>
}
